import { useState, useEffect } from 'react'
import { Link, useParams } from 'react-router-dom'
import { useQuery } from '@tanstack/react-query'
import { useTheme } from '../contexts/ThemeContext'
import { apiUrl } from '../utils/apiBase'
import { sanitizeProductName, sanitizeUrl } from '../utils/sanitize'
import Lottie from '../components/Lottie'
import './SharedCartPage.css'

// Service API pour les paniers partagés
const sharedCartService = {
  getByToken: async (token) => {
    const response = await fetch(apiUrl(`/api/shared/${token}`))
    if (!response.ok) {
      const error = await response.json()
      throw new Error(error.detail || 'Erreur lors de la récupération du panier')
    }
    return response.json()
  }
}

function SharedCartPage() {
  const { token } = useParams()
  const { theme } = useTheme()
  const [logoAnimation, setLogoAnimation] = useState(null)
  const [bagsAnimation, setBagsAnimation] = useState(null)
  const [moneyAnimation, setMoneyAnimation] = useState(null)
  const [calculatorAnimation, setCalculatorAnimation] = useState(null)

  // Charger les animations
  useEffect(() => {
    fetch('/Cadis_animation.json')
      .then(response => response.json())
      .then(data => setLogoAnimation(data))
      .catch(error => console.warn('Animation logo non trouvée:', error))
    
    fetch('/Bagsanim.json')
      .then(response => response.json())
      .then(data => setBagsAnimation(data))
      .catch(error => console.warn('Animation bags non trouvée:', error))
    
    fetch('/Bouseargent.json')
      .then(response => response.json())
      .then(data => setMoneyAnimation(data))
      .catch(error => console.warn('Animation money non trouvée:', error))
    
    fetch('/Calculatrice.json')
      .then(response => response.json())
      .then(data => setCalculatorAnimation(data))
      .catch(error => console.warn('Animation calculator non trouvée:', error))
  }, [])

  // Récupérer le panier partagé
  const { data: sharedCart, isLoading, error } = useQuery({
    queryKey: ['shared-cart', token],
    queryFn: () => sharedCartService.getByToken(token),
    retry: 1
  })

  const formatPrice = (price) => {
    return new Intl.NumberFormat('fr-FR', {
      style: 'currency',
      currency: 'EUR'
    }).format(price || 0)
  }

  if (isLoading) {
    return (
      <div className="shared-cart-page">
        <div className="shared-cart-loading">
          {logoAnimation && (
            <div className="loading-logo">
              <Lottie
                animationData={logoAnimation}
                loop
                autoplay
                style={{ width: '200px', height: '200px' }}
              />
            </div>
          )}
          <p>Chargement du panier partagé...</p>
        </div>
      </div>
    )
  }

  if (error) {
    return (
      <div className="shared-cart-page">
        <div className="shared-cart-error">
          <div className="error-icon">⚠️</div>
          <h2>Panier non disponible</h2>
          <p>{error.message}</p>
          <p className="error-hint">
            {error.message.includes('expiré') && 'Ce lien de partage a expiré.'}
            {error.message.includes('désactivé') && 'Ce lien de partage a été désactivé par son propriétaire.'}
            {error.message.includes('non trouvé') && 'Ce lien de partage n\'existe pas ou n\'est plus valide.'}
          </p>
        </div>
      </div>
    )
  }

  const budgetPercentage = sharedCart?.cart_budget 
    ? Math.min(100, (sharedCart.total / sharedCart.cart_budget) * 100)
    : 0

  return (
    <div className="shared-cart-page">
      <div className="shared-cart-container">
        {/* Header */}
        <div className="shared-cart-header">
          <div className="header-tagline-stairs">
            <div className="tagline-line line-1">Vous aussi,</div>
            <div className="tagline-line line-2">Partagez vos souhaits,</div>
            <div className="tagline-line line-3">Et ça ? seulement d'un clic !</div>
          </div>
          <div className="header-center">
            <div className="header-logo">
              <img src="/Logo Cam'it Blanc Horizontal.png" alt="Cam'it" className="camit-logo" />
            </div>
            <div className="header-cta">
              <Link to="/" className="get-started-btn">
                <span>Commencer</span>
              </Link>
            </div>
          </div>
        </div>

        {/* Informations du panier */}
        <div className="shared-cart-info">
          <h1 className="cart-name">{sharedCart?.cart_name}</h1>
          <div className="cart-meta">
            <div className="meta-item">
              <span className="meta-icon">
                {bagsAnimation ? (
                  <Lottie animationData={bagsAnimation} loop={true} autoplay={true} style={{ width: 28, height: 28 }} />
                ) : '📦'}
              </span>
              <span>{sharedCart?.item_count} article{sharedCart?.item_count > 1 ? 's' : ''}</span>
            </div>
            <div className="meta-item">
              <span className="meta-icon">
                {moneyAnimation ? (
                  <Lottie animationData={moneyAnimation} loop={true} autoplay={true} style={{ width: 28, height: 28 }} />
                ) : '💰'}
              </span>
              <span>Total : {formatPrice(sharedCart?.total)}</span>
            </div>
            {sharedCart?.cart_budget && (
              <div className="meta-item">
                <span className="meta-icon">
                  {calculatorAnimation ? (
                    <Lottie animationData={calculatorAnimation} loop={true} autoplay={true} style={{ width: 32, height: 32 }} />
                  ) : '🎯'}
                </span>
                <span>Budget : {formatPrice(sharedCart.cart_budget)}</span>
              </div>
            )}
          </div>

          {/* Barre de progression du budget */}
          {sharedCart?.cart_budget && (
            <div className="budget-progress-container">
              <div className="budget-progress-bar">
                <div 
                  className={`budget-progress-fill ${budgetPercentage > 100 ? 'over-budget' : ''}`}
                  style={{ width: `${Math.min(100, budgetPercentage)}%` }}
                ></div>
              </div>
              <div className="budget-progress-label">
                {budgetPercentage.toFixed(0)}% du budget utilisé
                {budgetPercentage > 100 && ` (+${formatPrice(sharedCart.total - sharedCart.cart_budget)} de dépassement)`}
              </div>
            </div>
          )}
        </div>

        {/* Liste des articles */}
        <div className="shared-cart-items">
          {sharedCart?.items && sharedCart.items.length > 0 ? (
            sharedCart.items.map((item) => (
              <div key={item.id} className="shared-item-card">
                {sanitizeUrl(item.image_url) && (
                  <div className="item-image">
                    <img src={sanitizeUrl(item.image_url)} alt={sanitizeProductName(item.name)} />
                  </div>
                )}
                <div className="item-details">
                  <h3 className="item-name">{sanitizeProductName(item.name)}</h3>
                  {sanitizeUrl(item.link) && (
                    <a 
                      href={sanitizeUrl(item.link)} 
                      target="_blank" 
                      rel="noopener noreferrer" 
                      className="item-link"
                    >
                      Voir le produit →
                    </a>
                  )}
                  <div className="item-meta">
                    <div className="item-price">
                      {formatPrice(item.price)}
                      {item.quantity > 1 && (
                        <span className="item-quantity"> × {item.quantity}</span>
                      )}
                    </div>
                    {item.quantity > 1 && (
                      <div className="item-total">
                        Total : {formatPrice(item.price * item.quantity)}
                      </div>
                    )}
                  </div>
                  {item.promo && (
                    <div className="item-promo">
                      🏷️ {item.promo}
                    </div>
                  )}
                </div>
              </div>
            ))
          ) : (
            <div className="empty-cart">
              <p>Ce panier est vide</p>
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="shared-cart-footer">
          <p className="footer-text">
            Ce panier a été partagé avec vous via <strong>Cam'it</strong>
          </p>
          <p className="footer-cta">
            <Link to="/" className="cta-link">
              Créer mon propre panier →
            </Link>
          </p>
        </div>
      </div>
    </div>
  )
}

export default SharedCartPage

